/**
 * Event handlers for Basecamp Todo Viewer
 * Extracted from BasecampTodoViewer for better separation of concerns
 */

import { toast } from 'sonner';
import type { BasecampTodo } from '@/types/basecamp';
import { fetchWithAuth } from '@/lib/api';
import { scheduleOpenIngestionConversation } from '@/lib/open-ingestion-conversation';
import { enqueueUpdateWorkSpecKickoff } from '@/lib/update-work-spec-kickoff';
import { dispatchBasecampTodoRefresh } from '@/lib/basecamp-ingestion-events';
import { resolveBasecampTodoIngestUrlFromTodo } from '@/components/layout/document-viewer.utils';
import { buildBasecampWebUrl, normalizeBasecampWebHost } from '@/lib/basecamp-external-url';

interface RelatedDocument {
  id: string;
  title: string;
  filePath: string;
}

interface HandlerParams {
  todo: BasecampTodo | null;
  projectId?: string;
  todoId: string;
  organizationId?: string;
  comments: any[];
  relatedDocs: RelatedDocument[];
  abTestData: { optionA: any; optionB: any } | null;
  abTestSessionId: string | null;
  diffData: { original: string; proposed: string; filename: string } | null;
  setAbTestData: (data: { optionA: any; optionB: any } | null) => void;
  setShowABTest: (show: boolean) => void;
  setIsLoadingABTest: (loading: boolean) => void;
  setDiffData: (data: { original: string; proposed: string; filename: string } | null) => void;
  setShowDiff: (show: boolean) => void;
  setIsApplyingDiff: (applying: boolean) => void;
  loadRelatedDocuments: () => Promise<void>;
  onDocumentClick?: (documentId: string, openInNewTab?: boolean | React.MouseEvent) => Promise<void>;
}

export function useTodoHandlers({
  todo,
  projectId,
  todoId,
  organizationId,
  comments,
  relatedDocs,
  abTestData,
  abTestSessionId,
  diffData,
  setAbTestData,
  setShowABTest,
  setIsLoadingABTest,
  setDiffData,
  setShowDiff,
  setIsApplyingDiff,
  loadRelatedDocuments,
  onDocumentClick
}: HandlerParams) {
  const handleIngestOrUpdate = async () => {
    if (!todo || !projectId) return;

    try {
      setIsLoadingABTest(true);

      const basecampUrl = resolveBasecampTodoIngestUrlFromTodo(todo, projectId, todoId);

      // Update flow: open existing work spec in main panel while ingestion runs in agent panel
      if (relatedDocs.length > 0 && onDocumentClick) {
        onDocumentClick(relatedDocs[0].id, true);
      }

      // Import the ingestBasecampTodo function (same as command palette)
      const {
        ingestBasecampTodo,
        applyUpdateWorkSpecModeUi,
        handleUpdateWorkSpecBlockedError,
        isAgentUpdateWorkSpecResult,
      } = await import('../../../../lib/api');

      const isUpdate = relatedDocs.length > 0;

      try {
        if (!isUpdate) {
          window.dispatchEvent(new CustomEvent('thinking-mode-event', {
            detail: {
              type: 'thinking_start',
              message: 'Starting Basecamp todo ingestion...'
            }
          }));
        }

        // Call AI ingestion endpoint (agent update or legacy create)
        const ingestionPromise = ingestBasecampTodo(basecampUrl, {
          forceRefresh: isUpdate,
          organizationId,
          ...(isUpdate ? { mode: 'agent' as const } : {}),
        });

        // Open chat panel immediately when conversationId is available (same as command palette)
        ingestionPromise.then((result) => {
          console.log('[useTodoHandlers] Ingestion result:', result);
          applyUpdateWorkSpecModeUi(result);
          scheduleOpenIngestionConversation(
            result.conversationId,
            result.threadId,
            result.title,
          );
          if (isAgentUpdateWorkSpecResult(result) && result.conversationId) {
            enqueueUpdateWorkSpecKickoff({
              targetConversationId: result.conversationId,
              targetThreadId: result.threadId,
              documentId: result.documentId,
              skillId: 'basecamp-todo',
            });
          }
        }).catch((error) => {
          if (handleUpdateWorkSpecBlockedError(error)) return;
          scheduleOpenIngestionConversation(
            error.conversationId,
            error.threadId,
          )
        });

        const result = await ingestionPromise;
        applyUpdateWorkSpecModeUi(result);

        // Check if it's a duplicate detection
        if (result.status === 'duplicate_detected') {
          // Chat panel already opened above - no toast needed
        } else if (result.success) {
          // Async ingestion returns immediately with status: 'processing'.
          // Work specs refresh via basecamp-ingestion-complete when chat polling finishes.
          if (result.status !== 'processing' && result.todoFile) {
            await loadRelatedDocuments();
            dispatchBasecampTodoRefresh({ todoId });

            if (onDocumentClick && result.todoFile) {
              setTimeout(() => {
                onDocumentClick(result.todoFile!);
              }, 2000);
            }
          }
          // No success toast - progress shown in chat
        } else {
          // Show error toast only if no conversation was created (chat will show error otherwise)
          if (!result.conversationId) {
            toast.error('Ingestion failed', {
              description: result.error || 'Unknown error',
              duration: 7000
            });
          }
        }

      } catch (ingestionError: any) {
        console.error('[useTodoHandlers] Ingestion failed:', ingestionError);

        if (handleUpdateWorkSpecBlockedError(ingestionError)) return;

        // Try to open chat panel if conversationId is in error response
        scheduleOpenIngestionConversation(
          ingestionError.conversationId,
          ingestionError.threadId,
        );

        // Check if this is a 401 authentication error
        const error = ingestionError as any;
        if (error.status === 401 && error.auth_url) {
          // Open Basecamp OAuth in new tab
          const authWindow = window.open(error.auth_url, 'basecamp-auth', 'width=600,height=700');

          // Show toast with instructions
          toast.info('Basecamp Authentication Required', {
            description: 'Please authorize in the popup window. Will retry after you authenticate.',
            duration: 10000,
          });

          // Poll for auth completion (check every 2 seconds)
          const authCheckInterval = setInterval(async () => {
            try {
              // Try to make a simple request to see if auth is complete
              const testResponse = await fetchWithAuth(`/api/basecamp/ingest-todo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: basecampUrl })
              });

              if (testResponse.ok || testResponse.status !== 401) {
                // Auth successful! Close window and retry
                clearInterval(authCheckInterval);
                authWindow?.close();

                toast.success('Authentication successful!', {
                  description: 'Retrying ingestion...',
                  duration: 2000
                });

                // Retry the ingestion
                setTimeout(() => handleIngestOrUpdate(), 1000);
              }
            } catch (e) {
              // Still not authed, keep polling
            }
          }, 2000);

          // Stop polling after 2 minutes
          setTimeout(() => {
            clearInterval(authCheckInterval);
            authWindow?.close();
          }, 120000);

          setIsLoadingABTest(false);
          return;
        }

        const errorMessage = ingestionError instanceof Error ? ingestionError.message : 'Unknown error';

        // Show error toast
        toast.error('Ingestion Failed', {
          description: errorMessage,
          duration: 8000,
          action: {
            label: 'Details',
            onClick: () => {
              console.log('Full error:', ingestionError);
              toast.info('Check Console', {
                description: 'Full error details logged to browser console'
              });
            }
          }
        });
      }

    } catch (error) {
      console.error('[useTodoHandlers] Unexpected error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Unexpected Error', {
        description: errorMessage
      });
    } finally {
      setIsLoadingABTest(false);
    }
  };

  const handleABTestSelect = async (choice: 'A' | 'B', reason?: string) => {
    if (!abTestData || !todo || !projectId) return;

    try {
      const selectedOption = choice === 'A' ? abTestData.optionA : abTestData.optionB;
      const selectedMarkdown = selectedOption.markdown;

      // Update session choice in database
      if (abTestSessionId) {
        try {
          await fetchWithAuth(`/api/ai/ingestion/ab-test-session/${abTestSessionId}/select`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ choice, reason })
          });
          console.log('[useTodoHandlers] Session choice updated:', choice);
        } catch (err) {
          console.warn('[useTodoHandlers] Failed to update session choice (non-fatal):', err);
        }
      }

      setShowABTest(false);

      // Check if there's an existing work spec for this todo
      let originalContent = '';
      let filename = '';

      if (relatedDocs.length > 0 && relatedDocs[0].filePath) {
        try {
          const response = await fetchWithAuth(
            `/api/documents/by-path?path=${encodeURIComponent(relatedDocs[0].filePath)}`
          );
          if (response.ok) {
            const doc = await response.json();
            originalContent = doc.content || '';
            filename = relatedDocs[0].filePath.split('/').pop() || 'work-spec.md';
          }
        } catch (err) {
          console.warn('[useTodoHandlers] Could not fetch existing document:', err);
        }
      } else {
        const suggestedSlug =
          selectedOption.frontmatter?.suggestedSlug ||
          selectedOption.frontmatter?.slug ||
          todo.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').substring(0, 50);
        filename = `${todoId}-${suggestedSlug}.md`;
        originalContent = '';
      }

      // Update session ID if present in abTestData
      const sessionIdFromData = (abTestData as any).sessionId || (abTestData as any).session_id;
      if (sessionIdFromData && !abTestSessionId) {
        // Note: This will be handled by parent component's useEffect
      }

      setDiffData({
        original: originalContent,
        proposed: selectedMarkdown,
        filename
      });
      setShowDiff(true);
    } catch (err) {
      console.error('[useTodoHandlers] Error processing selection:', err);
      toast.error('Failed to process selection');
    }
  };

  const handleDiffAccept = async () => {
    if (!diffData || !todo || !projectId) return;

    try {
      setIsApplyingDiff(true);

      const basecampUrl = resolveBasecampTodoIngestUrlFromTodo(todo, projectId, todoId);

      const isUpdate = diffData.original.length > 0;

      if (abTestSessionId) {
        try {
          const commentCount = comments.length;
          const todoContent = (todo as any).content || todo.description || '';
          const hasTechnicalTerms =
            todoContent.toLowerCase().includes('api') ||
            todoContent.toLowerCase().includes('database') ||
            todoContent.toLowerCase().includes('deploy');
          const todoType = commentCount === 0 ? 'simple' : hasTechnicalTerms ? 'technical' : 'complex';

          await fetchWithAuth(`/api/ai/ingestion/ab-test-session/${abTestSessionId}/applied`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ todoType })
          });
          console.log('[useTodoHandlers] Session marked as applied and preference stored');
        } catch (err) {
          console.warn('[useTodoHandlers] Failed to mark session as applied (non-fatal):', err);
        }
      }

      if (isUpdate && relatedDocs.length > 0 && relatedDocs[0].filePath) {
        const response = await fetchWithAuth(
          `/api/documents/by-path?path=${encodeURIComponent(relatedDocs[0].filePath)}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              content: diffData.proposed
            })
          }
        );

        if (!response.ok) {
          throw new Error('Failed to update document');
        }

        toast.success('Work spec updated', {
          description: 'Changes have been saved'
        });

        await loadRelatedDocuments();
        dispatchBasecampTodoRefresh({ todoId });

        if (onDocumentClick && relatedDocs[0].filePath) {
          await onDocumentClick(relatedDocs[0].filePath);
        }
      } else {
        const { ingestBasecampTodo } = await import('../../../../lib/api');
        await ingestBasecampTodo(basecampUrl, { organizationId });

        toast.success('Work spec created', {
          description: 'New document has been created'
        });

        await loadRelatedDocuments();
        dispatchBasecampTodoRefresh({ todoId });
      }

      setShowDiff(false);
      setDiffData(null);
    } catch (err) {
      console.error('[useTodoHandlers] Error applying changes:', err);
      toast.error('Failed to apply changes', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsApplyingDiff(false);
    }
  };

  const handleDiffReject = () => {
    setShowDiff(false);
    setDiffData(null);
    toast.info('Changes rejected');
  };

  const handleOpenInBasecamp = () => {
    if (todo?.web_url) {
      console.log('[useTodoHandlers] Opening in Basecamp (web_url):', todo.web_url);
      window.open(todo.web_url, '_blank');
      return;
    }

    if (todo?.app_url) {
      const accountMatch = todo.app_url.match(/https:\/\/3\.basecampapi\.com\/(\d+)\//);
      const accountId = accountMatch ? accountMatch[1] : null;

      if (accountId) {
        const url = todo.app_url.replace('basecampapi.com', 'basecamp.com').replace('.json', '');
        console.log('[useTodoHandlers] Opening in Basecamp (transformed app_url):', url);
        window.open(url, '_blank');
        return;
      }
    }

    if (projectId && todoId) {
      let accountId: string | null = null;
      if (todo?.app_url) {
        const accountMatch = todo.app_url.match(/\/\/(?:3|app)\.basecamp(?:api)?\.com\/(\d+)\//);
        accountId = accountMatch ? accountMatch[1] : null;
      }

      if (accountId) {
        const url = buildBasecampWebUrl(todoId, projectId, 'todo', accountId);
        console.log('[useTodoHandlers] Opening in Basecamp (constructed):', url);
        window.open(url, '_blank');
        return;
      }
    }

    if (todo?.url) {
      const url = normalizeBasecampWebHost(
        todo.url.replace('basecampapi.com', 'basecamp.com').replace('.json', '')
      );
      console.log('[useTodoHandlers] Opening in Basecamp (from url field):', url);
      window.open(url, '_blank');
      return;
    }

    console.warn('[useTodoHandlers] No URL available to open', { projectId, todoId, todo });
  };

  const handleProjectClick = (projectName: string | null) => {
    if (projectId) {
      window.dispatchEvent(
        new CustomEvent('integration-view-change', {
          detail: {
            tabId: `basecamp-project-${projectId}`,
            title: projectName || 'Basecamp Project',
            type: 'integration-basecamp',
            integrationData: {
              integrationId: 'basecamp',
              projectId: projectId.toString()
            }
          }
        })
      );
    }
  };

  return {
    handleIngestOrUpdate,
    handleABTestSelect,
    handleDiffAccept,
    handleDiffReject,
    handleOpenInBasecamp,
    handleProjectClick
  };
}

